import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';
import { TermsOfServiceModal, PrivacyPolicyModal } from './LegalModals.jsx';

function ConfidentialityAgreementView({ user, onAgreed, onDecline }) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [checkedBoxes, setCheckedBoxes] = useState({
    readTerms: false,
    understandPurpose: false,
    agreeConduct: false,
    agreeData: false,
    agreeBeta: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const scrollRef = React.useRef(null);

  const allChecked = Object.values(checkedBoxes).every(Boolean);
  const canSubmit = allChecked && hasScrolledToBottom;

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Scroll tracking
  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      if (atBottom) setHasScrolledToBottom(true);
    }
  };

  // Submit acceptance
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const agreementRecord = {
        userId: user.id,
        userName: user.name,
        userEmail: user.email,
        signedAt: new Date().toISOString(),
        agreementVersion: '2.0',
        agreementType: 'terms-and-conditions',
        checkboxes: { ...checkedBoxes },
        userAgent: navigator.userAgent.substring(0, 200),
      };

      await storage.set(`agreement-signed:${user.id}`, agreementRecord, true);
      onAgreed();
    } catch (err) {
      console.error('Error saving agreement:', err);
      setError('Failed to save your acceptance. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <img
              src={OWL_LOGO}
              alt="The ArguMentor Owl"
              className="w-16 h-16 drop-shadow-lg"
              style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' }}
            />
          </div>
          <h1 className="text-3xl font-light text-stone-800 tracking-tight">
            Terms & Conditions
          </h1>
          <p className="text-stone-500 font-light mt-1">
            Please read carefully and accept below to continue
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 overflow-hidden">
          {/* Scrollable Terms Content */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="p-6 sm:p-8 overflow-y-auto space-y-5 text-sm text-stone-700 leading-relaxed"
            style={{ maxHeight: '50vh' }}
          >
            <div className="text-center mb-6">
              <h2 className="text-lg font-bold text-stone-900 uppercase tracking-wide">
                The ArguMentor: Terms & Conditions
              </h2>
              <p className="text-stone-500 text-xs mt-1">Version 2.0 · Effective Date: {today}</p>
            </div>

            <p className="text-stone-700">
              Welcome to <strong>The ArguMentor</strong>, a product of <strong>The ArguMentor, LLC</strong> ("Company," "we," "us," or "our"),
              with its principal place of business in Land O' Lakes, Florida.
              By accessing or using The ArguMentor application (the "Service"), you agree to be bound by these
              Terms & Conditions. If you do not agree, please do not use the Service.
            </p>

            {/* Section 1 — What The ArguMentor IS */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <h3 className="font-bold text-emerald-900 mb-2">1. WHAT THE ARGUMETOR IS</h3>
              <p className="text-emerald-800 mb-2">
                The ArguMentor is a <strong>guided conflict resolution tool</strong> designed to promote and encourage
                healthy communication. It provides a structured framework for participants to work through
                disagreements constructively. The Service includes:
              </p>
              <p className="text-emerald-800 mb-1 pl-4">&#8226; A <strong>5-phase resolution process</strong>: Identify & Define, Communicate, Explore Alternatives, Evaluate & Select, and Agree & Implement</p>
              <p className="text-emerald-800 mb-1 pl-4">&#8226; <strong>Structured communication</strong> through paraphrasing exercises and "Because" statements</p>
              <p className="text-emerald-800 mb-1 pl-4">&#8226; <strong>Mentor oversight</strong>: peer facilitators who can observe, comment, and guide the process</p>
              <p className="text-emerald-800 mb-1 pl-4">&#8226; <strong>AI-assisted features</strong> such as paraphrase suggestions and "Say It Your Way" guided entry</p>
              <p className="text-emerald-800 mb-1 pl-4">&#8226; <strong>Mood tracking</strong> to help participants and mentors understand emotional patterns</p>
              <p className="text-emerald-800 pl-4">&#8226; <strong>Content safety monitoring</strong> with crisis resource display when concerning language is detected</p>
            </div>

            {/* Section 2 — What The ArguMentor IS NOT */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="font-bold text-red-900 mb-2">2. WHAT THE ARGUMETOR IS NOT</h3>
              <p className="text-red-800 mb-2">
                The ArguMentor is <strong>NOT</strong> any of the following, and should never be used as a substitute for:
              </p>
              <p className="text-red-800 mb-1 pl-4">&#8226; <strong>Mental health counseling or therapy</strong>: this is not a therapeutic tool</p>
              <p className="text-red-800 mb-1 pl-4">&#8226; <strong>Medical or psychological advice</strong>: we do not diagnose or treat any condition</p>
              <p className="text-red-800 mb-1 pl-4">&#8226; <strong>Legal services or mediation</strong>: this is not a legal forum and outcomes are not legally binding</p>
              <p className="text-red-800 mb-1 pl-4">&#8226; <strong>Crisis intervention</strong>: if you are in immediate danger, call <strong>911</strong></p>
              <p className="text-red-800 mb-1 pl-4">&#8226; <strong>Professional services of any kind</strong>: mentors are peer facilitators, not licensed professionals</p>
              <p className="text-red-800 pl-4">&#8226; <strong>A guaranteed resolution</strong>: the tool facilitates communication but cannot guarantee any outcome</p>
            </div>

            {/* Section 3 — Safety Notice */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" /> 3. SAFETY NOTICE
              </h3>
              <p className="text-amber-800 mb-2">
                If you or someone you know is in immediate danger or experiencing a medical or mental health emergency,
                <strong> call 911</strong> immediately.
              </p>
              <p className="text-amber-800">
                For emotional crisis support, contact the <strong>988 Suicide & Crisis Lifeline</strong> by calling or
                texting <strong>988</strong> (available 24/7). You can also text <strong>HOME</strong> to <strong>741741</strong> to
                reach the Crisis Text Line. The Service is not a crisis intervention tool and should
                not be relied upon in emergencies.
              </p>
            </div>

            {/* Section 4 — User Conduct */}
            <div>
              <h3 className="font-bold text-stone-900 mb-2">4. USER CONDUCT</h3>
              <p className="mb-2">
                By using The ArguMentor, you agree to communicate respectfully, refrain from threats or
                harassment, and engage in good faith during the conflict resolution process.
              </p>
              <p className="mb-2">You shall NOT:</p>
              <p className="mb-1 pl-4">(a) Use the Service to threaten, harass, intimidate, stalk, or abuse any person;</p>
              <p className="mb-1 pl-4">(b) Submit content that is defamatory, obscene, or promotes violence;</p>
              <p className="mb-1 pl-4">(c) Impersonate any person or entity;</p>
              <p className="mb-1 pl-4">(d) Attempt to gain unauthorized access to the Service or its systems;</p>
              <p className="pl-4">(e) Use the Service for any illegal purpose.</p>
            </div>

            {/* Section 5 — Data & Privacy */}
            <div>
              <h3 className="font-bold text-stone-900 mb-2">5. DATA SHARING & PRIVACY</h3>
              <p className="mb-2">
                <strong>5.1 Shared Data.</strong> Conflict data (including problem statements, perspectives,
                paraphrases, alternatives, and messages) is shared between all participants and mentors assigned
                to a conflict. This is core to the Service's functionality. Do not submit sensitive personal information
                (e.g., Social Security numbers, financial account details, medical records).
              </p>
              <p className="mb-2">
                <strong>5.2 Data Storage.</strong> Data is stored using Firebase Realtime Database and related
                cloud services provided by Google. The Company employs reasonable security measures but cannot
                guarantee absolute security of data in transit or at rest.
              </p>
              <p className="mb-2">
                <strong>5.3 Safety Monitoring.</strong> The Service includes automated content monitoring that scans text
                for language indicating potential self-harm or threats. If detected, crisis resources are displayed and
                assigned mentors may be notified. The system never blocks submissions.
              </p>
              <p>
                <strong>5.4 Full Details.</strong> For complete information about data collection, usage, retention, and your rights,
                please review our{' '}
                <button onClick={() => setShowPrivacyModal(true)} className="text-amber-700 hover:text-amber-800 underline font-medium">
                  Privacy Policy
                </button>.
              </p>
            </div>

            {/* Section 6 — AI Features */}
            <div>
              <h3 className="font-bold text-stone-900 mb-2">6. AI-POWERED FEATURES</h3>
              <p className="mb-2">
                The Service includes AI-powered features such as paraphrase suggestions and "Say It Your Way" guided
                conflict entry. AI-generated suggestions are provided as-is and may be inaccurate, incomplete, or
                inappropriate. You are solely responsible for reviewing and editing any AI-generated content before submission.
              </p>
              <p>
                AI features are optional aids and do not constitute professional advice.
              </p>
            </div>

            {/* Section 7 — Beta & Limitations */}
            <div>
              <h3 className="font-bold text-stone-900 mb-2">7. BETA STATUS & LIMITATIONS</h3>
              <p className="mb-2">
                The Service is currently in a beta/pre-release state and may contain bugs, errors, or incomplete features.
                The Service is provided <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> without warranties of any kind.
              </p>
              <p className="mb-2">
                <strong>Limitation of Liability.</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT BE
                LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE
                OF THE SERVICE. THE COMPANY'S TOTAL LIABILITY SHALL NOT EXCEED ONE HUNDRED DOLLARS ($100).
              </p>
              <p>
                The Company reserves the right to modify, suspend, or discontinue the Service at any time,
                with or without notice.
              </p>
            </div>

            {/* Section 8 — Intellectual Property */}
            <div>
              <h3 className="font-bold text-stone-900 mb-2">8. INTELLECTUAL PROPERTY</h3>
              <p className="mb-2">
                All intellectual property rights in the Service (including software, design, logos, trademarks,
                methodologies, and documentation) are the exclusive property of The ArguMentor, LLC.
                "The ArguMentor," the ArguMentor owl logo, and related marks are trademarks of the Company.
              </p>
              <p>
                You retain ownership of the content you submit. By submitting content, you grant the Company a
                non-exclusive, worldwide, royalty-free license to use, store, and process your content solely for
                providing and improving the Service.
              </p>
            </div>

            {/* Section 9 — General */}
            <div>
              <h3 className="font-bold text-stone-900 mb-2">9. GENERAL PROVISIONS</h3>
              <p className="mb-2">
                <strong>Governing Law.</strong> These Terms shall be governed by the laws of the State of Florida.
              </p>
              <p className="mb-2">
                <strong>Entire Agreement.</strong> These Terms, together with our{' '}
                <button onClick={() => setShowTermsModal(true)} className="text-amber-700 hover:text-amber-800 underline font-medium">
                  Terms of Service
                </button>{' '}and{' '}
                <button onClick={() => setShowPrivacyModal(true)} className="text-amber-700 hover:text-amber-800 underline font-medium">
                  Privacy Policy
                </button>, constitute the entire agreement between you and the Company.
              </p>
              <p className="mb-2">
                <strong>Contact.</strong> Questions may be directed to: <strong>info@4hci.com</strong>
              </p>
              <p>
                <strong>Age Requirement.</strong> You must be at least 13 years of age to use the Service. If you are under 18,
                your parent or legal guardian must review and agree to these Terms on your behalf.
              </p>
            </div>

            {/* Scroll indicator */}
            {!hasScrolledToBottom && (
              <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent pt-8 pb-2 text-center">
                <p className="text-amber-700 text-xs font-medium animate-bounce">
                  ↓ Scroll down to read the full terms ↓
                </p>
              </div>
            )}
          </div>

          {/* Acceptance Section */}
          <div className="border-t border-stone-200 p-6 sm:p-8 space-y-5 bg-stone-50">
            {/* Checkboxes */}
            <div className="space-y-3">
              {[
                { key: 'readTerms', label: 'I have read and understand these Terms & Conditions.' },
                { key: 'understandPurpose', label: 'I understand that The ArguMentor is a conflict resolution tool, NOT a mental health service, therapy, legal service, or substitute for any professional service.' },
                { key: 'agreeConduct', label: 'I agree to communicate respectfully, engage in good faith, and refrain from threats, harassment, or abusive language.' },
                { key: 'agreeData', label: 'I understand that conflict data is shared between assigned participants and mentors, and I consent to the data practices described above and in the Privacy Policy.' },
                { key: 'agreeBeta', label: 'I understand the Service is in beta, is provided "AS IS", and I accept the Limitation of Liability.' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={checkedBoxes[key]}
                    onChange={(e) => setCheckedBoxes(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 text-amber-700 border-stone-300 rounded focus:ring-amber-500 flex-shrink-0"
                  />
                  <span className="text-sm text-stone-700 group-hover:text-stone-900 transition-colors">{label}</span>
                </label>
              ))}
            </div>

            {/* Acceptor Info */}
            <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Name:</span>
                <span className="font-medium text-stone-900">{user.name}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Email:</span>
                <span className="font-medium text-stone-900">{user.email}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Date:</span>
                <span className="font-medium text-stone-900">{today}</span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Not scrolled warning */}
            {!hasScrolledToBottom && allChecked && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 text-sm">Please scroll through the entire terms before accepting.</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onDecline}
                className="flex-1 py-3 border border-stone-300 rounded-lg text-stone-600 hover:bg-stone-100 font-medium transition-colors"
              >
                Decline & Sign Out
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || saving}
                className="flex-1 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    I Accept
                  </>
                )}
              </button>
            </div>

            {/* Links to full legal docs */}
            <div className="flex items-center justify-center gap-4 text-xs text-stone-500">
              <button onClick={() => setShowTermsModal(true)} className="hover:text-amber-700 transition-colors">
                Full Terms of Service
              </button>
              <span>·</span>
              <button onClick={() => setShowPrivacyModal(true)} className="hover:text-amber-700 transition-colors">
                Privacy Policy
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-stone-500 text-xs mt-4">
          <a href="https://www.4hci.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-700 transition-colors">The ArguMentor, LLC</a>
          <span className="mx-2">·</span>
          Terms v2.0
        </p>
      </div>

      {/* Full legal document modals */}
      <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
    </div>
  );
}

export default ConfidentialityAgreementView;
